import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Download as DownloadIcon,
  Router as RouterIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import axios from 'axios';

const GatewayDownload = () => {
  const [packageInfo, setPackageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  // Get the base URL for curl command
  const getBaseUrl = () => {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:5000`;
  };

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('token') || '<YOUR_TOKEN>';
  };

  const getCurlCommand = () => {
    return `curl -H "Authorization: Bearer ${getToken()}" ${getBaseUrl()}/api/gateway-download -o edge-gateway.zip`;
  };

  const handleCopyCommand = () => {
    navigator.clipboard.writeText(getCurlCommand());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    loadPackageInfo();
  }, []);

  const loadPackageInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/gateway-download/info');
      setPackageInfo(response.data);
    } catch (err) {
      console.error('Error loading package info:', err);
      setError('Impossibile caricare le informazioni del pacchetto');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      setError(null);
      setDownloadSuccess(false);

      const response = await axios.get('/api/gateway-download', {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'edge-gateway.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 5000);
    } catch (err) {
      console.error('Error downloading package:', err);
      setError('Errore durante il download del pacchetto');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {downloadSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDownloadSuccess(false)}>
          Download completato! Estrai l'archivio sul PC gateway e segui le istruzioni nel README.
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <RouterIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6">
                Edge Gateway OPC UA
              </Typography>
              {packageInfo && (
                <Chip
                  label={`v${packageInfo.version}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pacchetto per installare un Gateway OPC UA su un PC dedicato nella rete di produzione.
            Il gateway funge da ponte tra le macchine OPC UA e il sistema cloud.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Contenuto del pacchetto:
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Codice sorgente Edge Gateway"
                secondary="Applicazione Node.js con client OPC UA"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Docker Compose standalone"
                secondary="Configurazione pronta per il deployment"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="README con istruzioni"
                secondary="Guida passo-passo per l'installazione"
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownload}
              disabled={downloading || !packageInfo?.available}
              size="large"
            >
              {downloading ? 'Download in corso...' : 'Scarica Pacchetto ZIP'}
            </Button>

            <Button
              variant="outlined"
              startIcon={<InfoIcon />}
              href="/docs/GATEWAY_OPCUA.md"
              target="_blank"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://github.com/your-repo/industria4/blob/main/docs/GATEWAY_OPCUA.md', '_blank');
              }}
            >
              Documentazione
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Requisiti:</strong> Docker e Docker Compose installati sul PC gateway.
              Connettività verso questo server e verso le macchine OPC UA.
            </Typography>
          </Alert>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Download via curl:
          </Typography>

          <Box
            sx={{
              backgroundColor: '#1e1e1e',
              borderRadius: 1,
              p: 2,
              position: 'relative',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: '#d4d4d4',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}
          >
            <Button
              size="small"
              onClick={handleCopyCommand}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                minWidth: 'auto',
                color: copied ? '#4caf50' : '#888'
              }}
            >
              <CopyIcon fontSize="small" />
              {copied && <Typography variant="caption" sx={{ ml: 0.5 }}>Copiato!</Typography>}
            </Button>
            {getCurlCommand()}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Usa questo comando per scaricare il pacchetto direttamente da terminale sul PC gateway.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GatewayDownload;
